import React, { useMemo } from 'react';
import {
  useGridApiRef,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import styled from 'styled-components';
import { Person, Row } from 'talent_matching/types';
import HeaderRow from 'talent_matching/components/header_row';
import FilterRow from 'talent_matching/components/filters/filter_row';
import CandidateSidePanel from 'talent_matching/components/side_panel/candidate_side_panel';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import RankingLoader from 'talent_matching/components/table/ranking_loader';
import CalibrationModal from 'talent_matching/components/calibration/calibration_modal';
import NoResultsOverlay from 'talent_matching/components/no_results_overlay';
import useQueryHelper from 'talent_matching/hooks/use_query_helper';
import { useColumns } from 'talent_matching/components/table/columns';
import BaseDataTable from 'talent_matching/components/table/base_data_table';
import DataViewTableFooter from 'talent_matching/components/table/data_view_table_footer';
import { useColumnState } from 'talent_matching/hooks/use_column_state';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import { RejectModalProvider } from '../../../application_review/components/reject_modal_context';
import { ACTIONS_COLUMN_FIELD } from 'talent_matching/components/table/column_visibility_defaults';
import { AdvancedCandidateCountProvider } from 'talent_matching/contexts/advanced_candidate_count_context';
import { RowActionsModalProvider } from 'talent_matching/contexts/row_actions_modal_context';
import useSidePanel from 'talent_matching/hooks/use_side_panel';
import useQuestionColumns from 'talent_matching/hooks/use_question_columns';
import SyncedRow from 'talent_matching/components/table/synced_row';

const NON_TOGGLABLE_COLUMNS = ['fullName', ACTIONS_COLUMN_FIELD]; // TODO: This should be shared with the other tables

type Props = {
  rows: Row[];
  totalRows: number;
  loading: boolean;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowSelected: (selectedRows: GridRowSelectionModel) => void;
  selectionsHelper: SelectionsHelper;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  preSelectedRow: Row | null;
  setPreSelectedRow: (row: Row | null) => void;
};

export const MinHeightStack = styled(Stack)`
  min-height: 750px;
`;

const Wrapper = styled.div`
  .highlighted-row {
    background-color: ${FullPalette.GREY_E500};
  }
`;

const DataViewTable = ({
  rows,
  totalRows,
  loading,
  rowsPerPage,
  currentPage,
  onPageChange,
  onRowSelected,
  selectionsHelper,
  columnVisibilityModel,
  setColumnVisibilityModel,
  preSelectedRow,
  setPreSelectedRow,
}: Props) => {
  const { canBulkEdit } = useTalentMatchingTabContext();

  const [showCalibrationModal, openCalibrationModal, closeCalibrationModal] =
    useModal();
  const apiRef = useGridApiRef();

  const { questionColumns, columnVisibilityModelWithQuestionDefaults } =
    useQuestionColumns({
      rows,
      columnVisibilityModel,
      setColumnVisibilityModel,
    });

  const { handleColumnChange } = useColumnState(
    apiRef,
    columnVisibilityModelWithQuestionDefaults
  );

  const { excludePeople, invalidatePeople } = useQueryHelper();

  const {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    candidatePrefetchBatch,
    handlePanelToggle,
    handleRowClick,
    getRowClassName,
    navigateAfterRemoval,
  } = useSidePanel({ rows, preSelectedRow, setPreSelectedRow });

  const { rankingInProgress, isBackgroundRanking } = useRankingInProgress();
  const userInitiatedRankingInProgress =
    rankingInProgress && !isBackgroundRanking;
  const { wrapperRef } = useStickyGridHeader({
    disabled: userInitiatedRankingInProgress,
  });

  const onMergeSuccess = () => {
    if (isPanelOpen && selectedRow?.id === selectedRow?.id) {
      navigateAfterRemoval();
    }
    invalidatePeople();
  };

  const onRejectSuccess = (row: Row, closeModal: () => void) => {
    closeModal();

    if (isPanelOpen && row.id === selectedRow?.id) {
      navigateAfterRemoval();
    }

    excludePeople([row.id]);
  };

  const columns = useColumns({
    selectionsHelper,
    isPanelOpen,
    selectedRow,
    openCalibrationModal,
    onRejectSuccess,
    questionColumns,
  });

  const columnSelectorColumns = useMemo(
    () =>
      columns
        .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
        .map((c) => ({
          field: c.field,
          label: c.headerName || c.field,
        })),
    [columns]
  );

  return (
    <AdvancedCandidateCountProvider openCalibrationModal={openCalibrationModal}>
      <RowActionsModalProvider onRejectSuccess={onRejectSuccess}>
        <FullWidth>
          <MinHeightStack spacing={2}>
            <HeaderRow
              selectionsHelper={selectionsHelper}
              apiRef={apiRef}
              openCalibrationModal={openCalibrationModal}
              columnSelectorColumns={columnSelectorColumns}
              columnVisibilityModel={columnVisibilityModelWithQuestionDefaults}
              setColumnVisibilityModel={setColumnVisibilityModel}
            />
            {canBulkEdit && <FilterRow />}
            {userInitiatedRankingInProgress ? (
              <RankingLoader />
            ) : (
              <Wrapper ref={wrapperRef}>
                <BaseDataTable
                  rows={rows}
                  columns={columns}
                  totalRows={totalRows}
                  loading={loading}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  onPageChange={onPageChange}
                  onRowSelected={onRowSelected}
                  apiRef={apiRef}
                  columnVisibilityModel={
                    columnVisibilityModelWithQuestionDefaults
                  }
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                  onColumnWidthChange={handleColumnChange}
                  onRowClick={(row: RowDef) => handleRowClick(row as Person)}
                  getRowClassName={getRowClassName}
                  footer={() => (
                    <DataViewTableFooter rowsPerPage={rowsPerPage} />
                  )}
                  slots={{
                    noRowsOverlay: NoResultsOverlay,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    row: SyncedRow as React.ComponentType<any>,
                  }}
                />
              </Wrapper>
            )}
          </MinHeightStack>

          {selectedRow && (
            <RejectModalProvider>
              <CandidateSidePanel
                ref={popoverRef}
                onClose={() => setIsPanelOpen(false)}
                onToggle={handlePanelToggle}
                applicationId={selectedRow.id}
                isVisible={isPanelOpen}
                currentIndex={selectedRowIndex}
                rowCount={rows.length}
                previousCandidate={previousRow}
                nextCandidate={nextRow}
                candidatePrefetchBatch={candidatePrefetchBatch}
                selectCandidate={handleRowClick}
                openCalibrationModal={openCalibrationModal}
                calibrationModalOpen={showCalibrationModal}
                onRejectSuccess={onRejectSuccess}
                onMergeSuccess={onMergeSuccess}
                onAdvanceSuccess={() => {
                  if (isPanelOpen) {
                    navigateAfterRemoval();
                  }
                }}
              />
            </RejectModalProvider>
          )}

          <CalibrationModal
            isOpen={showCalibrationModal}
            onClose={closeCalibrationModal}
          />
        </FullWidth>
      </RowActionsModalProvider>
    </AdvancedCandidateCountProvider>
  );
};

export default DataViewTable;
